// src/components/features/campaigns/CampaignKPIsTab.jsx
import React, { useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Menu,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
  LinearProgress,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  BarChart as KpiIcon,
  Insights as InsightsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  MoreVert as MoreVertIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Import services and hooks
import useCampaignStore from '../../stores/campaignStore';
import useNotificationStore from '../../stores/notificationStore';
import useAuthStore from '../../stores/authStore';

// KPI categories
const KPI_CATEGORIES = [
  { value: 'awareness', label: 'Awareness', color: 'primary' },
  { value: 'engagement', label: 'Engagement', color: 'secondary' },
  { value: 'conversion', label: 'Conversion', color: 'success' },
  { value: 'retention', label: 'Retention', color: 'info' },
  { value: 'other', label: 'Other', color: 'default' }
];

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

// Format time helper
const formatTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

// Initial KPI update form
const initialUpdateForm = {
  current: 0,
};

// CampaignKPIsTab component
const CampaignKPIsTab = ({ campaign }) => {
  const theme = useTheme();
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [updateForm, setUpdateForm] = useState(initialUpdateForm);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedKpiIndex, setSelectedKpiIndex] = useState(null);
  const navigate = useNavigate();
  // Hooks
  const { updateCampaignKPIs } = useCampaignStore();
  const { showSuccess, showError } = useNotificationStore();
  const { hasPermission } = useAuthStore();
  
  // Get campaign KPIs
  const kpis = campaign?.kpis || [];
  
  // Check if user can update KPIs
  const canUpdateKPIs = hasPermission('campaigns', 'update');
  
  // Open update dialog
  const handleOpenUpdateDialog = (index) => {
    handleCloseMenu();
    if (index === null || index === undefined) return;
    
    const kpi = kpis[index];
    setUpdateForm({
      current: kpi.current || 0,
    });
    setSelectedKpiIndex(index);
    setOpenUpdateDialog(true);
  };
  
  // Close update dialog
  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
    setUpdateForm(initialUpdateForm);
    setSelectedKpiIndex(null);
  };
  
  // Handle update form change
  const handleUpdateFormChange = (e) => {
    const { name, value } = e.target;
    setUpdateForm(prev => ({
      ...prev,
      [name]: name === 'current' ? parseFloat(value) || 0 : value
    }));
  };
  
  // Submit KPI update
  const handleSubmitUpdate = async () => {
    if (selectedKpiIndex === null) return;
    
    try {
      // Create update object with KPI ID and current value
      const kpiUpdate = {
        id: kpis[selectedKpiIndex]._id,
        current: updateForm.current
      };
      
      await updateCampaignKPIs(campaign._id, [kpiUpdate]);
      showSuccess('KPI updated successfully');
      handleCloseUpdateDialog();
    } catch (error) {
      showError(error.message || 'Failed to update KPI');
    }
  };
  
  // Handle milestone menu
  const handleOpenMenu = (event, index) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedKpiIndex(index);
  };
  
  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setSelectedKpiIndex(null);
  };
  
  // Calculate progress percentage for a KPI
  const calculateKpiProgress = (kpi) => {
    if (!kpi.target || kpi.target === 0) return 0;
    
    // Some KPIs are better when lower (like cost metrics)
    // For these, progress is better when current is lower than target
    const isInversed = kpi.name.toLowerCase().includes('cost') || 
                       kpi.name.toLowerCase().includes('churn') ||
                       kpi.name.toLowerCase().includes('bounce');
    
    if (isInversed) {
      // For inverse metrics, 0 is 100% of target
      if (kpi.current <= 0) return 100;
      // If current > target, we're over the "bad" target
      if (kpi.current >= kpi.target) return 0;
      // Otherwise calculate inverse percentage
      return Math.round(((kpi.target - kpi.current) / kpi.target) * 100);
    } else {
      // For standard metrics
      const percentage = Math.round((kpi.current / kpi.target) * 100);
      // Cap at 100% for display purposes
      return Math.min(percentage, 100);
    }
  };
  
  // Get KPI progress color
  const getKpiProgressColor = (kpi) => {
    const progress = calculateKpiProgress(kpi);
    
    if (progress >= 100) return 'success';
    if (progress >= 70) return 'info';
    if (progress >= 30) return 'warning';
    return 'error';
  };
  
  // Get KPI trend icon
  const getKpiTrendIcon = (kpi) => {
    if (!kpi.tracking || !kpi.tracking.previousValue) {
      return <TrendingFlatIcon />;
    }
    
    const isInversed = kpi.name.toLowerCase().includes('cost') || 
                      kpi.name.toLowerCase().includes('churn') ||
                      kpi.name.toLowerCase().includes('bounce');
    
    const current = kpi.current || 0;
    const previous = kpi.tracking.previousValue || 0;
    
    if (current === previous) {
      return <TrendingFlatIcon color="info" />;
    }
    
    if (isInversed) {
      // For inverse metrics, lower is better
      return current < previous ? 
        <TrendingUpIcon color="success" /> : 
        <TrendingDownIcon color="error" />;
    } else {
      // For standard metrics, higher is better
      return current > previous ? 
        <TrendingUpIcon color="success" /> : 
        <TrendingDownIcon color="error" />;
    }
  };
  
  // Group KPIs by category
  const kpiGroups = CATEGORIES => {
    return KPI_CATEGORIES.map(category => ({
      ...category,
      kpis: kpis.filter(kpi => kpi.category === category.value)
    }));
  };
  
  // Sort KPIs by progress
  const sortedKpiGroups = kpiGroups().sort((a, b) => {
    // Sort by number of KPIs first (most to least)
    return b.kpis.length - a.kpis.length;
  });
  
  // Filter out empty categories
  const filteredKpiGroups = sortedKpiGroups.filter(group => group.kpis.length > 0);
  
  // Calculate overall KPI progress
  const calculateOverallProgress = () => {
    if (kpis.length === 0) return 0;
    
    let progressSum = 0;
    for (const kpi of kpis) {
      progressSum += calculateKpiProgress(kpi);
    }
    
    return Math.round(progressSum / kpis.length);
  };
  
  // Overall progress
  const overallProgress = calculateOverallProgress();
  
  return (
    <Grid container spacing={3}>
      {/* KPIs Overview Card */}
      <Grid item xs={12}>
        <Card elevation={0} variant="outlined">
          <CardHeader 
            title="Campaign KPIs" 
            avatar={<KpiIcon  />}
            action={
              canUpdateKPIs && (
                <Button
                  variant="contained"
                  
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/campaigns/${campaign._id}/kpis/create`)}
                >
                  Add KPI
                </Button>
              )
            }
          />
          <Divider />
          <CardContent>
            {kpis.length === 0 ? (
              <Box sx={{ 
                p: 3, 
                textAlign: 'center', 
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 1,
                border: `1px dashed ${theme.palette.divider}`
              }}>
                <Typography color="textSecondary" gutterBottom>
                  No KPIs defined yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Define key performance indicators to measure your campaign's success
                </Typography>
                {canUpdateKPIs && (
                  <Button
                    variant="contained"
                    
                    startIcon={<AddIcon />}
                    onClick={() => navigate(`/campaigns/${campaign._id}/kpis/create`)}
                    sx={{ mt: 2 }}
                  >
                    Add KPIs
                  </Button>
                )}
              </Box>
            ) : (
              <>
                {/* Overall Progress */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Overall KPI Progress
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h5" sx={{ mr: 2 }}>
                      {overallProgress}%
                    </Typography>
                    <Box sx={{ flexGrow: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={overallProgress} 
                        color={
                          overallProgress >= 90 ? 'success' :
                          overallProgress >= 60 ? 'info' :
                          overallProgress >= 30 ? 'warning' : 'error'
                        }
                        sx={{ height: 10, borderRadius: 1 }}
                      />
                    </Box>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Based on average progress across all {kpis.length} KPIs
                  </Typography>
                </Box>
                
                {/* KPIs by Category */}
                {filteredKpiGroups.map((group) => (
                  <Box key={group.value} sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Chip 
                        label={group.label} 
                        color={group.color} 
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="subtitle1">
                        KPIs ({group.kpis.length})
                      </Typography>
                    </Box>
                    
                    <Grid container spacing={2}>
                      {group.kpis.map((kpi, index) => {
                        const progress = calculateKpiProgress(kpi);
                        const actualIndex = kpis.findIndex(k => k._id === kpi._id);
                        
                        return (
                          <Grid item xs={12} md={6} key={kpi._id || index}>
                            <Card 
                              variant="outlined" 
                              sx={{ height: '100%' }}
                            >
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                    {kpi.name}
                                  </Typography>
                                  {canUpdateKPIs && (
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleOpenMenu(e, actualIndex)}
                                    >
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                                
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                  {kpi.description || 'No description'}
                                </Typography>
                                
                                <Box sx={{ mt: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" sx={{ width: 60 }}>
                                      Target:
                                    </Typography>
                                    <Typography variant="subtitle2">
                                      {kpi.target} {kpi.unit}
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="body2" sx={{ width: 60 }}>
                                      Current:
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ mr: 1 }}>
                                      {kpi.current || 0} {kpi.unit}
                                    </Typography>
                                    {getKpiTrendIcon(kpi)}
                                  </Box>
                                  
                                  <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                      <Typography variant="body2">
                                        Progress: {progress}%
                                      </Typography>
                                      {kpi.tracking?.lastUpdated && (
                                        <Tooltip title={`Last updated: ${formatDate(kpi.tracking.lastUpdated)} ${formatTime(kpi.tracking.lastUpdated)}`}>
                                          <Typography variant="caption" color="textSecondary">
                                            Updated {formatDate(kpi.tracking.lastUpdated)}
                                          </Typography>
                                        </Tooltip>
                                      )}
                                    </Box>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={progress} 
                                      color={getKpiProgressColor(kpi)}
                                      sx={{ height: 6, borderRadius: 1 }}
                                    />
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      </Grid>
      
      {/* KPI Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => handleOpenUpdateDialog(selectedKpiIndex)}>
          <ListItemIcon>
            <UpdateIcon fontSize="small" />
          </ListItemIcon>
          Update Current Value
        </MenuItem>
        <MenuItem onClick={handleCloseMenu}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit KPI
        </MenuItem>
        <MenuItem onClick={handleCloseMenu}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          View History
        </MenuItem>
        <MenuItem onClick={handleCloseMenu}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography color="error">Delete</Typography>
        </MenuItem>
      </Menu>
      
      {/* Update KPI Dialog */}
      <Dialog open={openUpdateDialog} onClose={handleCloseUpdateDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          Update KPI Value
        </DialogTitle>
        <DialogContent dividers>
          {selectedKpiIndex !== null && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  {kpis[selectedKpiIndex]?.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Current value: {kpis[selectedKpiIndex]?.current || 0} {kpis[selectedKpiIndex]?.unit}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Target value: {kpis[selectedKpiIndex]?.target} {kpis[selectedKpiIndex]?.unit}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="New Value"
                  name="current"
                  type="number"
                  value={updateForm.current}
                  onChange={handleUpdateFormChange}
                  fullWidth
                  required
                  margin="normal"
                  InputProps={{
                    endAdornment: kpis[selectedKpiIndex]?.unit && (
                      <InputAdornment position="end">
                        {kpis[selectedKpiIndex].unit}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitUpdate} 
            variant="contained" 
            
          >
            Update Value
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default CampaignKPIsTab;